import type { CreasePattern } from './types';
import { isCut } from './types';
import type { PlanarTopology } from '@/geometry/dcel';
import { buildPlanarTopology, halfEdgeTarget } from '@/geometry/dcel';
import { heading2, sub2 } from '@/geometry/vec2';
import { wrapAngle2Pi } from '@/constants/numeric';

export interface VertexTopology {
  index: number;
  /** Incident edge ids, counter-clockwise around the vertex. */
  edges: number[];
  /** Neighbour vertex ids, in the same counter-clockwise order. */
  neighbours: number[];
  /** Sector angles between consecutive creases, radians, same order.
   *  sectors[i] is the angle from edges[i] to edges[i+1] (wrapping). */
  sectors: number[];
  degree: number;
  /** True when the vertex is surrounded by paper on all sides. */
  interior: boolean;
  /** Why the vertex is not interior, when it is not. */
  boundaryReason?: 'on-boundary' | 'cut' | 'dangling' | 'isolated';
}

export interface DerivedTopology {
  planar: PlanarTopology;
  vertices: VertexTopology[];
  /** Face ids on each side of each edge. */
  edgeFaces: [number, number][];
  /** Interior edges that have a face on both sides - the hinge candidates. */
  interiorEdges: number[];
}

/**
 * Derive adjacency, sector angles and interiority from a planarised pattern.
 *
 * Sector angles are measured counter-clockwise, so for an interior vertex they
 * sum to exactly 2*pi (up to round-off). Kawasaki's theorem is stated on this
 * ordering; see src/origami-math/kawasaki.ts.
 */
export function deriveTopology(pattern: CreasePattern): DerivedTopology {
  const planar = buildPlanarTopology(pattern.verticesCoords, pattern.edgesVertices);
  const nV = pattern.verticesCoords.length;

  // A vertex is on the paper's outer rim if any of its half-edges belongs to an
  // unbounded cycle (halfEdgeFace === -1).
  const vertices: VertexTopology[] = [];
  for (let v = 0; v < nV; v++) {
    const outgoing = planar.vertexHalfEdges[v];
    const edges = outgoing.map((h) => h >> 1);
    const neighbours = outgoing.map((h) => halfEdgeTarget(h, pattern.edgesVertices));
    const degree = edges.length;

    const sectors: number[] = [];
    if (degree >= 1) {
      const angles = neighbours.map((n) =>
        heading2(sub2(pattern.verticesCoords[n], pattern.verticesCoords[v])),
      );
      for (let i = 0; i < degree; i++) {
        const a = angles[i];
        const b = angles[(i + 1) % degree];
        sectors.push(degree === 1 ? 2 * Math.PI : wrapAngle2Pi(b - a));
      }
    }

    let interior = degree >= 2;
    let boundaryReason: VertexTopology['boundaryReason'];
    if (degree === 0) {
      interior = false;
      boundaryReason = 'isolated';
    } else if (degree === 1) {
      interior = false;
      boundaryReason = 'dangling';
    } else if (outgoing.some((h) => planar.halfEdgeFace[h] < 0)) {
      interior = false;
      boundaryReason = 'on-boundary';
    } else if (edges.some((e) => isCut(pattern.edgesAssignment[e]))) {
      interior = false;
      boundaryReason = 'cut';
    } else if (edges.some((e) => pattern.edgesAssignment[e] === 'B')) {
      interior = false;
      boundaryReason = 'on-boundary';
    }

    vertices.push({
      index: v,
      edges,
      neighbours,
      sectors,
      degree,
      interior,
      ...(boundaryReason ? { boundaryReason } : {}),
    });
  }

  const interiorEdges: number[] = [];
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    const [l, r] = planar.edgeFaces[e];
    if (l >= 0 && r >= 0 && !isCut(pattern.edgesAssignment[e])) interiorEdges.push(e);
  }

  return { planar, vertices, edgeFaces: planar.edgeFaces, interiorEdges };
}
